import React, { useState, useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { 
  Button, Spin, Form, Input, Select, Space, Splitter, theme, Row, Col, Switch, Alert, notification, Modal, Typography
} from 'antd';
import { ArrowLeftOutlined, FileTextOutlined, CodeOutlined, EyeOutlined, ExpandOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import FormInteractiveBuilder from './FormInteractiveBuilder.component';
import { createForm, updateForm, getFormDetails } from '../../actions/formActions';
import { ThemeContext } from '../../index';
import FloatingSaveBar from '../clinical/FloatingSaveBar';

const INITIAL_SCHEMA = {
  name: 'New Form',
  version: '1.0.0',
  published: false,
  pages: [{
    label: 'Page 1',
    sections: [{
      label: 'Section 1',
      isExpanded: 'true',
      questions: []
    }]
  }]
};

const ENTITY_TYPES = [
  { label: 'Patient', value: 'Patient' },
  { label: 'Occurrence', value: 'Occurrence' },
  { label: 'Clinical Staging', value: 'ClinicalStaging' },
  { label: 'CNS Contact', value: 'CnsContact' },
  { label: 'Complication', value: 'Complication' },
  { label: 'Consent', value: 'Consent' },
  { label: 'Investigation', value: 'Investigation' },
  { label: 'Pathology', value: 'Pathology' },
  { label: 'Patient Address', value: 'PatientAddress' },
  { label: 'Presenting Symptom', value: 'PresentingSymptom' },
  { label: 'Recurrence', value: 'Recurrence' },
  { label: 'Functional Outcomes', value: 'Repatriation' },
  { label: 'Risk Factor', value: 'RiskFactor' },
  { label: 'Surveillance', value: 'Surveillance' },
  { label: 'Transfer', value: 'Transfer' },
  { label: 'Surgical Treatment', value: 'SurgicalTreatment' },
  { label: 'Non-Surgical Treatment', value: 'NonSurgicalTreatment' },
  { label: 'Treatment Type', value: 'TreatmentType' },
  { label: 'Tumour Site', value: 'TumourSite' },
  { label: 'Encounter', value: 'Encounter' }
];

const FormPublisher = ({ 
  formId, 
  onSave, 
  defaultEntityType,
  // Redux state props
  loadingDetails = false,
  loadingCreate = false,
  loadingUpdate = false,
  errorCreate,
  errorUpdate,
  formDetails,
  // Actions
  onCreateForm,
  onUpdateForm,
  onGetFormDetails,
  // Layout props
  showBackButton = true,
  showPadding = true
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { themeConfig } = useContext(ThemeContext);
  
  const [schemaJson, setSchemaJson] = useState('');
  const [parsedSchema, setParsedSchema] = useState(null);
  const [showSchemaEditor, setShowSchemaEditor] = useState(false);
  const [builderMaximized, setBuilderMaximized] = useState(false);
  const [displayError, setDisplayError] = useState(null);
  const [sidebarOffset, setSidebarOffset] = useState(185); // Default to expanded sidebar width
  
  const isDarkMode = themeConfig?.algorithm === theme.darkAlgorithm;

  // Calculate sidebar offset based on actual layout - responsive
  useEffect(() => {
    const calculateSidebarOffset = () => {
      const isMobile = window.innerWidth <= 768;
      
      // On mobile, no offset needed
      if (isMobile) {
        setSidebarOffset(0);
        return;
      }
      
      // Find the content wrapper div (the one with padding: 24px)
      // This is our target - we want the save button to align with its left edge
      const contentDiv = document.querySelector('div[style*="padding: 24px"]');
      if (contentDiv) {
        const rect = contentDiv.getBoundingClientRect();
        const offset = Math.round(rect.left);
        if (offset > 0) {
          setSidebarOffset(offset);
          return;
        }
      }
      
      // Fallback: Calculate based on sidebar width
      const sidebarCollapsed = window.innerWidth <= 768 ? false : (
        document.querySelector('[style*="width: 48px"]') !== null ||
        document.querySelector('[style*="collapsedWidth: 48"]') !== null
      );
      
      const sidebarWidth = sidebarCollapsed ? 48 : 185;
      const contentPadding = 24;
      setSidebarOffset(sidebarWidth + contentPadding);
    };

    // Initial calculation with delay to ensure DOM is ready
    setTimeout(calculateSidebarOffset, 100);
    
    // Listen for window resize - debounced
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateSidebarOffset, 150);
    };
    window.addEventListener('resize', handleResize);
    
    // Check periodically for sidebar state changes (collapsed/expanded)
    const interval = setInterval(calculateSidebarOffset, 500);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(interval);
      clearTimeout(resizeTimeout);
    };
  }, []);
  
  const loading = formId && loadingDetails;
  const saving = loadingCreate || loadingUpdate;

  // Initialize form
  useEffect(() => {
    if (formId) {
      if (onGetFormDetails) {
        onGetFormDetails(formId);
      } else {
        dispatch(getFormDetails(formId));
      }
    } else {
      initializeNewForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const initializeNewForm = () => {
    const initialJson = JSON.stringify(INITIAL_SCHEMA, null, 2);
    setSchemaJson(initialJson);
    setParsedSchema(INITIAL_SCHEMA);
    form.setFieldsValue({
      code: '',
      name: 'New Form',
      version: '1.0.0',
      isActive: true,
      isPublished: false,
      autoSave: true,
      entityType: defaultEntityType || 'Patient'
    });
  };

  // Load existing form
  useEffect(() => {
    if (formId && formDetails && !loadingDetails) {
      const formData = formDetails;
      const schema = JSON.parse(formData.schemaJson || '{}');
      
      setSchemaJson(JSON.stringify(schema, null, 2));
      setParsedSchema(schema);
      form.setFieldsValue({
        code: formData.code,
        name: formData.name,
        version: formData.version,
        isActive: formData.isActive ?? true,
        isPublished: formData.isPublished ?? false,
        autoSave: formData.autoSave ?? true,
        entityType: formData.entityType || 'Patient'
      });
    }
  }, [formId, formDetails, loadingDetails, form]);

  // Handle schema changes
  const handleSchemaChange = (newSchemaJson) => {
    setSchemaJson(newSchemaJson);
    try {
      const parsed = JSON.parse(newSchemaJson);
      setParsedSchema(parsed);
    } catch (error) {
      setParsedSchema(null);
    }
  };

  // Save form
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      JSON.parse(schemaJson); // Validate JSON
      
      const payload = {
        code: values.code,
        name: values.name,
        schemaJson: schemaJson,
        version: values.version,
        isActive: values.isActive ?? true,
        isPublished: values.isPublished ?? false,
        autoSave: values.autoSave ?? true,
        entityType: values.entityType || 'Patient'
      };

      if (formId) {
        if (onUpdateForm) {
          onUpdateForm(formId, payload);
        } else {
          dispatch(updateForm(formId, payload));
        }
      } else {
        if (onCreateForm) {
          onCreateForm(payload);
        } else {
          dispatch(createForm(payload));
        }
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        notification.error({ message: 'Invalid JSON schema' });
      } else {
        notification.error({ message: 'Please fill required fields' });
      }
    }
  };

  // Handle errors - following CRUD pattern
  useEffect(() => {
    if (saving) setDisplayError(null);
    if (errorCreate && errorCreate !== displayError) {
      setDisplayError(errorCreate);
    }
  }, [errorCreate, displayError, saving]);

  useEffect(() => {
    if (saving) setDisplayError(null);
    if (errorUpdate && errorUpdate !== displayError) {
      setDisplayError(errorUpdate);
    }
  }, [errorUpdate, displayError, saving]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="Loading form..." />
      </div>
    );
  }

  return (
    <div style={{ padding: showPadding ? 24 : 0 }}>
      {showBackButton && (
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/settings/forms')}
          style={{ marginBottom: 16 }}
        >
          Back to Forms
        </Button>
      )}
      
      {/* Error Display */}
      {displayError && (
        <Alert
          message="Validation Error"
          description={Array.isArray(displayError) 
            ? displayError.map((error, index) => <p key={index}>{error}</p>)
            : <p>{displayError}</p>
          }
          type="error"
          closable
          showIcon
          onClose={() => setDisplayError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Form Metadata */}
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="code"
              label="Form Code"
              rules={[
                { required: true, message: 'Required' },
                { pattern: /^[a-z0-9-]+$/, message: 'Lowercase, numbers, hyphens only' }
              ]}
            >
              <Input placeholder="e.g., risk-factors" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="name" label="Form Name" rules={[{ required: true }]}>
              <Input placeholder="e.g., Risk Factors" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="entityType" label="Entity Type">
              <Select options={ENTITY_TYPES} showSearch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item name="version" label="Version" rules={[{ required: true }]}>
              <Input placeholder="1.0.0" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={8} sm={8} md={6}>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={8} sm={8} md={6}>
            <Form.Item name="isPublished" label="Published" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={8} sm={8} md={6}>
            <Form.Item name="autoSave" label="Auto Save" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Schema Editor & Builder */}
      <Splitter style={{ height: 'calc(100vh - 200px)', minHeight: '85vh', border: '1px solid #d9d9d9', borderRadius: 8 }}>
        {showSchemaEditor && (
          <Splitter.Panel defaultSize="35%" min="30%" max="50%">
            <div style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <strong>Schema Editor</strong>
                <Button size="small" icon={<EyeOutlined />} onClick={() => setShowSchemaEditor(false)}>
                  Back to Visual Builder
                </Button>
              </div>
              <AceEditor
                mode="json"
                theme={isDarkMode ? 'monokai' : 'github'}
                value={schemaJson}
                onChange={handleSchemaChange}
                name="schema_editor"
                editorProps={{ $blockScrolling: true }}
                setOptions={{ useWorker: false, showLineNumbers: true, tabSize: 2 }}
                style={{ width: '100%', flex: 1 }}
              />
            </div>
          </Splitter.Panel>
        )}
        
        <Splitter.Panel defaultSize={showSchemaEditor ? "65%" : "100%"}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ 
              padding: '12px 16px', 
              borderBottom: '1px solid #d9d9d9',
              backgroundColor: isDarkMode ? '#1f1f1f' : '#fafafa',
              display: 'flex',
              justifyContent: 'space-between',
              flexShrink: 0
            }}>
              <strong>Visual Form Builder</strong>
              <Space size={8}>
                {!showSchemaEditor && (
                  <Button size="small" icon={<CodeOutlined />} onClick={() => setShowSchemaEditor(true)}>
                    Show Schema
                  </Button>
                )}
                <Button size="small" icon={<ExpandOutlined />} onClick={() => setBuilderMaximized(true)}>
                  Maximize
                </Button>
              </Space>
            </div>
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {builderMaximized ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography.Text type="secondary">
                    Builder is open maximized — close the modal to return here.
                  </Typography.Text>
                </div>
              ) : (
                <FormInteractiveBuilder 
                  schema={parsedSchema}
                  onSchemaChange={(newSchema) => handleSchemaChange(JSON.stringify(newSchema, null, 2))}
                  showPreview={true}
                  formName={form.getFieldValue('name')}
                />
              )}
            </div>
          </div>
        </Splitter.Panel>
      </Splitter>

      <Modal
        open={builderMaximized}
        onCancel={() => setBuilderMaximized(false)}
        footer={null}
        width="100vw"
        style={{ top: 0, maxWidth: '100vw', paddingBottom: 0, margin: 0 }}
        styles={{
          header: {
            padding: '12px 16px',
            margin: 0,
            backgroundColor: isDarkMode ? '#1f1f1f' : '#fafafa',
            borderBottom: '1px solid #d9d9d9',
          },
          body: { height: 'calc(100vh - 55px)', padding: 0, overflow: 'hidden' },
          content: { borderRadius: 0 },
        }}
        destroyOnClose
        zIndex={1100}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
            <strong>Visual Form Builder</strong>
            {!showSchemaEditor && (
              <Button size="small" icon={<CodeOutlined />} onClick={() => setShowSchemaEditor(true)}>
                Show Schema
              </Button>
            )}
          </div>
        }
      >
        <Splitter style={{ height: '100%' }}>
          {showSchemaEditor && (
            <Splitter.Panel defaultSize="35%" min="30%" max="50%">
              <div style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <strong>Schema Editor</strong>
                  <Button size="small" icon={<EyeOutlined />} onClick={() => setShowSchemaEditor(false)}>
                    Show Visual Builder
                  </Button>
                </div>
                <AceEditor
                  mode="json"
                  theme={isDarkMode ? 'monokai' : 'github'}
                  value={schemaJson}
                  onChange={handleSchemaChange}
                  name="schema_editor_maximized"
                  editorProps={{ $blockScrolling: true }}
                  setOptions={{ useWorker: false, showLineNumbers: true, tabSize: 2 }}
                  style={{ width: '100%', flex: 1 }}
                />
              </div>
            </Splitter.Panel>
          )}
          <Splitter.Panel defaultSize={showSchemaEditor ? '65%' : '100%'}>
            <FormInteractiveBuilder
              schema={parsedSchema}
              onSchemaChange={(newSchema) => handleSchemaChange(JSON.stringify(newSchema, null, 2))}
              showPreview={true}
              formName={form.getFieldValue('name')}
            />
          </Splitter.Panel>
        </Splitter>
      </Modal>

      <FloatingSaveBar
        onSave={handleSave}
        saving={saving}
        hasChanges={true}
        saveText="Save Form"
        layout="left"
        leftOffset={sidebarOffset}
      />
    </div>
  );
};

export default FormPublisher;
